// src/layouts/AdminPortal/DataSourceMonitoring/DataSourceHandlers.ts
import { message } from "antd";
import { AxiosError } from "axios";
import type { DataSource } from "./DataSourceColumns";
import {
  getConnectedSources,
  syncNow,
  viewConnection,
  deleteConnectedSource,
} from "src/services/AdminPortalAPIs";

// 1. Fetch all data sources
export const fetchConnectedSources = async (
  setAllSources: (data: DataSource[]) => void,
  setFilteredSources: (data: DataSource[]) => void,
  setLoading: (value: boolean) => void
) =>{
  setLoading(true);
  try {
    const res = await getConnectedSources();
    if (res.data?.status) {
      setAllSources(res.data.data);
      setFilteredSources(res.data.data);
    } else {
      message.error("Failed to fetch connected sources");
    }
  } catch (error) {
     console.error(error);
    message.error("Something went wrong!");
  } finally {
    setLoading(false);
  }
};

// 2. Handle sync
export const handleSyncNow = async (
  user_email: string,
  source_name: string,
  tenant_name: string,
  setActionInProgressId: (val: string | null) => void,
  refreshData: () => void
) => {
  const actionId = `${user_email}-${source_name}`;
  setActionInProgressId(actionId);
  try {
    const res = await syncNow({ user_email, source_name });
    const sourceLabel = `${source_name} (${tenant_name})`;

    if (res.data?.success?.length > 0)
      {message.success(`Manual sync initiated for ${sourceLabel}`);}
    else if (res.data?.errors?.length > 0)
      {message.error(`Some pipelines failed to sync for ${sourceLabel}`);}
    else {message.info(`Sync completed for ${sourceLabel}`);}
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const errorMessage = axiosError.response?.data?.message || "Sync failed";
    message.error(errorMessage);
  } finally {
    setActionInProgressId(null);
    refreshData();
  }
};

// 3. View connection
export const handleViewConnection = async (
  user_email: string,
  source_name: string,
  setActionInProgressId: (val: string | null) => void,
  setConnectionDetails: (data: any) => void,
  setViewModalVisible: (val: boolean) => void
) => {
  const actionId = `${user_email}-${source_name}`;
  setActionInProgressId(actionId);
  try {
    const res = await viewConnection({ user_email, source_name });
    if (res.data?.status) {
      setConnectionDetails(res.data);
      setViewModalVisible(true);
    } else {
      message.error("Failed to fetch connection details");
    }
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const errorMessage =
      axiosError.response?.data?.message || "Failed to fetch connection";
    message.error(errorMessage);
  } finally {
    setActionInProgressId(null);
  }
};

// 4. Handle delete
export const handleDeleteSource = async (
  selectedItem: any,
  setActionInProgressId: (val: string | null) => void,
  refreshData: () => void,
  onCloseModal: () => void
) => {
  const actionId = `${selectedItem.tenant_email}-${selectedItem.connected_source}`;
  setActionInProgressId(actionId);
  const payload = {
    user_email: selectedItem.tenant_email,
    source_name: selectedItem.connected_source.toLowerCase(),
  };
  try {
    const res = await deleteConnectedSource(payload);
    if (res.data?.status) {
      message.success("Source deleted successfully.");
      refreshData();
    } else {
      message.error(res.data?.message || "Failed to delete source.");
    }
  } catch {
    message.error("Error deleting source. Please try again.");
  } finally {
    onCloseModal();
    setActionInProgressId(null);
  }
};
